<template>
  <div class="ysb-cascader-mobile" :class="rootClasses">
    <Field
      ref="field"
      v-model="fieldValue"
      is-link
      :border="true"
      :label="label"
      :placeholder="placeholder"
      :clearable="allowClear && !disabled"
      :disabled="disabled"
      clear-trigger="always"
      @click="handleFieldClick"
      @clear="handleClear"
    />
    <Popup
      v-model:show="show"
      round
      position="bottom"
      style="height: 80%"
      @close="handlePopupClose"
    >
      <div class="title">
        <div>
          {{ title }}
          <span class="selected-text">已选：{{ selectedTextInTitle }}</span>
        </div>
        <Close @click="handleClose" />
      </div>
      <Search
        v-if="filterable"
        v-model="searchKey"
        :placeholder="searchPlaceholder"
        @search="handleFilter"
      />
      <div v-show="filtering" class="filter-result">
        <div
          v-for="(res, index) of filterResult"
          :key="index"
          class="filter-item"
          @click="filterResultClick(res)"
        >
          <div>
            <span
              v-for="(text, strIndex) of getFilterResultMap(res.text, searchKey)"
              :key="strIndex"
              :class="{ 'matched-text': text.matched }"
            >
              {{ text.str }}
            </span>
          </div>
          <img v-if="!res.isLeaf" src="../../_assets/right-arrow.svg" />
        </div>
        <div v-show="remoteSearchLoading" class="loading">
          <Loading type="spinner" />
        </div>
        <div v-show="showEmptyImg" class="empty-img">
          <div v-show="!remoteSearchLoading">
            <img src="../../_assets/empty-image.png" />
            <div class="text">暂无相关数据</div>
          </div>
        </div>
      </div>
      <Cascader
        v-show="!filtering"
        v-model="cascaderValue"
        :field-names="vantFieldNames"
        :options="options"
        :active-color="activeColor"
        :show-header="false"
        @close="show = false"
        @change="onChange"
        @finish="onFinish"
      >
      </Cascader>
      <div v-if="props.changeOnSelect" class="confirm-btn">
        <Button type="primary" block @click="handleConfirm">确定</Button>
      </div>
    </Popup>
  </div>
</template>

<script lang="ts">
export default {
  name: 'YCascaderMobile'
}
</script>
<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useVModel } from '@vueuse/core'
import { Field, Popup, Cascader, Search, Loading, Button } from 'vant'
import 'vant/es/field/style/index'
import 'vant/es/popup/style/index'
import 'vant/es/cascader/style/index'
import 'vant/es/search/style/index'
import 'vant/es/loading/style/index'
import 'vant/es/button/style/index'
import { cascaderProps, Option } from './cascader'
import Close from '../../icons/close.vue'
import type { CascaderProps } from './cascader'
import type { ShowSearchType } from 'ant-design-vue/es/cascader'

const props = defineProps(cascaderProps)
const emit = defineEmits(['update:value', 'change'])
const modelValue = useVModel(props, 'value', emit)

const labelKey = props.fieldNames.label ?? 'label'
const valueKey = props.fieldNames.value ?? 'value'
const childrenKey = props.fieldNames.children ?? 'children'
const vantFieldNames = {
  text: labelKey,
  value: valueKey,
  children: childrenKey
}

const rootClasses = computed(() => ({
  'has-confirm-btn': props.changeOnSelect,
  filter: props.filter
}))

const fieldValue = ref(props.value.filter((item) => item).join('/'))
const show = ref(false)
const cascaderValue = ref(props.value[props.value.length - 1])
const selectedOptionsUseInConfirm = ref<Option[]>([])

const selectedTextInTitle = computed(() => {
  const len = selectedOptionsUseInConfirm.value.length
  return len > 0 ? selectedOptionsUseInConfirm.value[len - 1][labelKey] : ''
})

function onChange({ selectedOptions }: any) {
  selectedOptionsUseInConfirm.value = selectedOptions

  if (!props.loadData) return

  new Promise<Option[]>((resolve) => {
    props.loadData!(selectedOptions, resolve)
  }).then((options) => {
    options.forEach((option) => {
      if (option.isLeaf === false) {
        option[childrenKey] = []
        option.className = 'has-child'
      }
    })
    if (options.length > 0) selectedOptions[selectedOptions.length - 1][childrenKey] = options
  })
}

function onFinish({ selectedOptions }: any) {
  if (props.changeOnSelect) return
  show.value = false
  updateModel(selectedOptions)
}

function handleConfirm() {
  show.value = false
  updateModel(selectedOptionsUseInConfirm.value)
}

function handleClear() {
  updateModel([])
  cascaderValue.value = ''
  selectedOptionsUseInConfirm.value = []
}

function handleClose() {
  show.value = false
}

const selfUpdate = ref(false)
updateModel(getSelectedOptionsfromModelValue(props.value))

function updateModel(selectedOptions: Option[], isEmitChangeEvent = true) {
  if (!props.options.length) return
  const labels = selectedOptions.map((option) => option[labelKey])
  fieldValue.value = props.displayRender
    ? props.displayRender({ labels, selectedOptions })
    : selectedOptions.map((option: Option) => option[labelKey]).join('/')

  selfUpdate.value = true
  modelValue.value = selectedOptions.map((option: Option) => option[valueKey])
  cascaderValue.value = selectedOptions.length
    ? selectedOptions[selectedOptions.length - 1][valueKey]
    : ''
  if (isEmitChangeEvent) {
    emit('change', selectedOptions)
  }
}

watch(modelValue, (val) => {
  if (selfUpdate.value) {
    selfUpdate.value = false
    return
  }
  const selectedOptions = getSelectedOptionsfromModelValue(val)
  updateModel(selectedOptions, false)
  selectedOptionsUseInConfirm.value = selectedOptions
})

watch(
  () => props.options,
  () => {
    const selectedOptions = getSelectedOptionsfromModelValue(modelValue.value)
    updateModel(selectedOptions)
    selectedOptionsUseInConfirm.value = selectedOptions
  }
)

// 根据绑定value值获取已选项数组
function getSelectedOptionsfromModelValue(value: Array<string | number>): Option[] {
  let result: Option[] = []
  let currentOptions = props.options
  for (let i = 0; i < value.length; i++) {
    const matchedOption = currentOptions.find((option) => option[valueKey] === value[i])
    if (!matchedOption) {
      result = []
      break
    }
    result.push(matchedOption)
    currentOptions = matchedOption[childrenKey] ?? []
  }
  return result
}

const field = ref<HTMLInputElement | null>(null)
function handleFieldClick() {
  field.value?.blur()
  if (!props.disabled) show.value = true
}

// 搜索
const filterable = computed(() => props.filter || props.remoteSearch)
const searchKey = ref('')
const filter: ShowSearchType['filter'] =
  typeof props.filter === 'function'
    ? props.filter
    : (inputValue, path) => {
        return path.some((option) =>
          option[labelKey]?.toLowerCase().includes(inputValue.toLowerCase())
        )
      }

interface FilterResultItem {
  text: string
  selectedOptions: Option[]
  isLeaf: boolean
}
const filterResult = ref<FilterResultItem[]>([])
const filtering = ref(false)
const remoteSearchLoading = ref(false)
const maxFilterResult = 40
watch(searchKey, (keyword) => {
  handleFilter(keyword)
})

async function handleFilter(keyword: string) {
  if (!keyword) {
    filtering.value = false
    return
  }
  filterResult.value = []
  filtering.value = true
  if (props.remoteSearch) {
    remoteSearchLoading.value = true
    try {
      await new Promise<void>((resolve) => {
        props.remoteSearch!(keyword, resolve)
      })
    } catch (error) {
      console.error(error)
    }
    formatOptions(props.options)
    remoteSearchLoading.value = false
  }
  const result: FilterResultItem[] = []
  backtrack(props.options, [], result, keyword)
  filterResult.value = result
}

// 通过回溯遍历处理整个树
function backtrack(
  options: CascaderProps['options'] | undefined,
  tempList: Option[],
  result: FilterResultItem[],
  keyword: string
) {
  if (props.changeOnSelect) appendResult()
  if (!options || !options.length) {
    if (!props.changeOnSelect) appendResult()
    return
  }
  for (let i = 0; i < options.length; i++) {
    if (result.length >= maxFilterResult) break
    tempList.push(options[i])
    backtrack(options[i][childrenKey], tempList, result, keyword)
    tempList.pop()
  }
  function appendResult() {
    if (filter!(keyword, tempList, {})) {
      result.push({
        text: tempList.map((option) => option[labelKey]).join('/'),
        selectedOptions: [...tempList],
        isLeaf: !options || !options.length
      })
    }
  }
}

function filterResultClick(item: FilterResultItem) {
  filtering.value = false
  searchKey.value = ''
  updateModel([])
  nextTick(() => {
    updateModel(item.selectedOptions)
    selectedOptionsUseInConfirm.value = item.selectedOptions
    show.value = false
  })
}

function getFilterResultMap(
  text: string,
  target: string
): Array<{ str: string; matched?: boolean }> {
  if (!target) return []
  let index = text.indexOf(target)
  if (index < 0) return [{ str: text }]
  return [
    { str: text.slice(0, index) },
    { str: target, matched: true },
    ...getFilterResultMap(text.slice(index + target.length), target)
  ]
}

// 当optins为异步获取时，首次改变时format
const syncFirstOptionsGet = ref(false)
watch(
  () => props.options,
  (options) => {
    if (!syncFirstOptionsGet.value) {
      syncFirstOptionsGet.value = true
      formatOptions(options)
    }
  }
)

// 处理options将isLeaf转换为[]，有子选项的加上'has-child'类名
formatOptions(props.options)
function formatOptions(options: Option[]) {
  if (props.loadData) {
    transformIsLeaf(options)
  }
  addClassInOption(options)
}
function addClassInOption(options: Option[]) {
  options.forEach((option) => {
    if (option[childrenKey]) {
      option.className = 'has-child'
      addClassInOption(option[childrenKey])
    }
  })
}
function transformIsLeaf(options: Option[]) {
  options.forEach((option) => {
    if (option.isLeaf === false) {
      option[childrenKey] = []
    }
    if (option[childrenKey]?.length) transformIsLeaf(option[childrenKey])
  })
}

const showEmptyImg = computed(() => {
  return filtering.value && filterResult.value.length < 1 && !remoteSearchLoading.value
})

const popupCloseAnimationTime = 300

function handlePopupClose() {
  if (!isModelValueEqualCurrentSelected()) {
    const selectedOptions = getSelectedOptionsfromModelValue(modelValue.value)
    selectedOptionsUseInConfirm.value = selectedOptions
    // 延时改变选中值，改变选中值是为了重置选项
    // 防止在弹窗关闭过程中进行选项的变化，时间为弹窗动画时间
    setTimeout(() => {
      updateModel([])
      nextTick(() => updateModel(selectedOptions))
    }, popupCloseAnimationTime)
  }
}

// 判断绑定值和当前弹窗选择项是否一致
function isModelValueEqualCurrentSelected() {
  if (modelValue.value.length !== selectedOptionsUseInConfirm.value.length) {
    return false
  }
  return modelValue.value.every((value) => {
    return selectedOptionsUseInConfirm.value.find((option) => value === option[valueKey])
  })
}
</script>
